import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { pick } from 'ramda'
import debounce from 'lodash.debounce'
import { selectors as configSelectors } from 'mage-config'
import { selectors as coreSelectors } from 'mage-core'
import * as i18nSelectors from 'mage-i18n/src/selectors'
import * as selectors from '../selectors'
import useLazyRef from '../hooks/useLazyRef'
import useGoogleAnalytics from './useGoogleAnalytics'
import useEducationFeatures from './useEducationFeatures'
import handleEducationError from '../handleEducationError'

export default function useEducationAnalytics({
  category,
  action,
  label,
  payload,
  referenceId,
  referenceType,
  googleAnalytics = true,
  trackOnMount = false,
  skip = false,
  debounce: debounceTimeout = 0
}) {
  const client = useSelector(selectors.educationClient)
  const app = useSelector(coreSelectors.clientId)
  const region = useSelector(configSelectors.region)
  const lang = useSelector(i18nSelectors.language)
  const sessionId = useSelector(coreSelectors.sessionId)
  const isMerchantsRealm = useSelector(coreSelectors.isMerchantsRealm)
  const features = useEducationFeatures()

  const trackWithGoogleAnalytics = useGoogleAnalytics({
    category,
    action,
    label
  })

  const submitDataDebounced = useLazyRef(() =>
    debounceTimeout === 0 ? submitData : debounce(submitData, debounceTimeout)
  )

  useEffect(() => {
    if (trackOnMount && features) {
      track()
    }
  }, [trackOnMount, skip, label, features])

  return track

  function track(extraData) {
    const data = {
      eventAt: new Date(),
      app,
      path: window.location.pathname,
      region,
      lang,
      sessionId,
      category,
      action,
      label,
      payload,
      referenceId,
      referenceType,
      targetedContentFeature: featureValue(features, 'meg.uhqm.targeted-content'),
      ...extraData
    }

    return submitDataDebounced(
      client,
      googleAnalytics,
      trackWithGoogleAnalytics,
      data,
      isMerchantsRealm,
      skip
    )
  }
}

function submitData(
  client,
  googleAnalytics,
  trackWithGoogleAnalytics,
  data,
  isMerchantsRealm,
  skip
) {
  if (isMerchantsRealm && !skip) {
    const analyticsData = pick(['category', 'action', 'label'], data)

    googleAnalytics && trackWithGoogleAnalytics(analyticsData)

    return trackWithEducationAnalytics(client, data)
  }
}

function trackWithEducationAnalytics(client, data) {
  return client
    .request({
      method: 'POST',
      url: `/v1/analytics`,
      data
    })
    .catch(handleEducationError)
}

function featureValue(features, featureId) {
  const feature = (features && features[featureId]) || {}
  return feature.variate_id || feature.is_enabled
}
